---
title: Privacy Policy
---

<script lang="ts">
	import LegalContact from '$lib/components/LegalContact.svelte';
	import PrivacyPolicyIntro from '$lib/components/PrivacyPolicyIntro.svelte';

	export let data;
</script>

# Privacy Policy

Our Privacy Policy was last updated on September 7, 2025.

<PrivacyPolicyIntro config={data.config} />

## Personal Identification Information

We may collect personal identification information from Users in a variety of ways, including when they:

- Visit our Site
- Subscribe to our newsletter or communications
- Request information about our services
- Engage our consulting services
- Purchase or download our digital products
- Contact us through our website

Users may be asked for information such as:
- Name and email address
- Company information (for consulting services)
- Billing information (for purchases)
- Phone number (for service inquiries)

Users may also visit our Site anonymously. We will collect personal identification information from Users only if they voluntarily provide it. Users can always choose not to provide such information, though it may prevent them from accessing certain features of the Site, receiving our services, or obtaining digital products.

## Non-Personal Identification Information

We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include:

- Browser name and version
- Type of computer and operating system
- Internet service provider
- IP address
- Pages visited and time spent on our Site
- Referring website information
- Other similar technical information

## Web Browser Cookies

Our Site may use 'cookies' to enhance User experience. User's web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. User may choose to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note that some parts of the Site may not function properly.

We may use cookies for:
- Remembering user preferences
- Analyzing site traffic and usage patterns
- Improving site functionality
- Personalizing content

## How We Use Collected Information

Our Site may collect and use Users' personal information for the following purposes:

### To Improve Customer Service
Information you provide helps us respond to your customer service requests and support needs more efficiently.

### To Personalize User Experience
We may use aggregated information to understand how Users as a group interact with our Site, services, and digital products.

### To Process Transactions
We may use the information Users provide about themselves when placing an order or engaging our services only to provide service to that order or engagement. We do not share this information with outside parties except to the extent necessary to provide the service.

### To Send Periodic Communications
We may use the email address to send Users information and updates related to their orders, service engagements, as well as to respond to inquiries or requests. If a User opts in to our mailing list, they may receive emails that include:

- Updates about our services
- Information about new digital products
- Industry insights and thought leadership content
- Promotional materials (with clear opt-out options)

Each email will include clear unsubscribe instructions, and Users may also contact us through the Site to be removed from future communications.

### To Provide Consulting Services
For consulting engagements, we may use personal and business information to:
- Understand your business needs and objectives
- Deliver customized advisory services
- Maintain project documentation
- Provide ongoing support and follow-up

## How We Protect Your Information

We adopt appropriate data collection, storage and processing practices, and security measures to protect against unauthorized access, alteration, disclosure or destruction of your personal information, including:

- Secure data transmission (SSL/TLS encryption)
- Regular security assessments
- Access controls and authentication
- Secure data storage and backup procedures
- Employee training on data protection

## Sharing Your Personal Information

We do not sell, trade, or rent Users' personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners, trusted affiliates and advertisers for the purposes outlined above.

We may share personal information in the following limited circumstances:

- With your explicit consent
- To comply with legal obligations
- To protect our rights and prevent fraud
- With service providers who assist us in operating our business (under strict confidentiality agreements)
- In connection with a business transfer or acquisition

## Data Retention

We retain personal information only as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. For consulting services, we may retain project-related information for business and legal purposes.

## Third Party Websites

Users may find advertising or other content on our Site that links to third-party websites. We do not control the content or links that appear on these sites and are not responsible for the practices employed by websites linked to or from our Site. These websites may have their own privacy policies and terms. Browsing and interaction on any other website, including websites that link to our Site, are subject to that website's own policies.

## International Data Transfers

If you are accessing our Site from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States where our servers are located and our central database is operated.

## Your Rights

Depending on your location, you may have certain rights regarding your personal information, including:

- The right to access your personal information
- The right to correct inaccurate information
- The right to delete your personal information
- The right to restrict or object to processing
- The right to data portability
- The right to withdraw consent

To exercise these rights, please contact us using the information provided below.

## Children's Privacy

Our Site is not directed to children under the age of 13, and we do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.

## Changes to This Privacy Policy

The Company has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the top of this page. We encourage Users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect. You acknowledge and agree that it is your responsibility to review this privacy policy periodically and become aware of modifications.

## Your Acceptance of These Terms

By using this Site, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Site. Your continued use of the Site following the posting of changes to this policy will be deemed your acceptance of those changes.

## Contact Us

If you have questions about this Privacy Policy or wish to exercise your rights regarding your personal information, please contact us at:

<LegalContact config={data.config} />

---

*This Privacy Policy is effective as of September 7, 2025, and applies to all users of our website and recipients of our services and products.*
